import { useState } from "react";
import axios from "axios";
import { GraduationCap, Loader2 } from "lucide-react";

export const TutorOnboarding = ({ api, authConfig, onDone, initial }) => {
  const [name, setName] = useState(initial?.student_name || "");
  const [cls, setCls] = useState(initial?.class_level || null);
  const [board, setBoard] = useState(initial?.board || "cbse");
  const [language, setLanguage] = useState(initial?.language || "english");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function save() {
    if (!name.trim() || !cls) { setErr("Please enter the student's name and pick a class."); return; }
    setSaving(true); setErr("");
    try {
      const r = await axios.post(`${api}/tutor/profile`, {
        student_name: name.trim(), class_level: cls, board,
        language: board === "tnboard" ? language : "english",
      }, authConfig);
      onDone(r.data.profile);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save — try again.");
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-10" data-testid="tutor-onboarding">
      <div className="w-full max-w-md">
        <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center mb-6 mx-auto">
          <GraduationCap className="text-cyan-300" size={26}/>
        </div>
        <h1 className="text-3xl font-black text-white text-center mb-2">Meet your AI teacher</h1>
        <p className="text-zinc-400 text-sm text-center mb-8">Tell us about the student so lessons match their board, class and subjects.</p>

        <label className="block mb-5">
          <span className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-2">Student's name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. Ananya"
            data-testid="onboarding-name-input"
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-xl text-white focus:border-cyan-400 focus:outline-none"/>
        </label>

        <span className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-2">Board</span>
        <div className="grid grid-cols-2 gap-2 mb-5">
          {[["cbse", "CBSE"], ["tnboard", "Tamil Nadu State Board"]].map(([b, label]) => (
            <button key={b} onClick={() => setBoard(b)} data-testid={`onboarding-board-${b}`}
              className={`py-3 px-2 rounded-xl border font-bold text-sm transition-colors ${board === b ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
              {label}
            </button>
          ))}
        </div>

        {board === "tnboard" && (
          <>
            <span className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-2">Teacher speaks in</span>
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="onboarding-language-row">
              {[["english", "English"], ["tamil", "தமிழ் (Tamil)"]].map(([l, label]) => (
                <button key={l} onClick={() => setLanguage(l)} data-testid={`onboarding-lang-${l}`}
                  className={`py-3 rounded-xl border font-bold text-sm transition-colors ${language === l ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        <span className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-2">Class</span>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-6">
          {[6, 7, 8, 9, 10, 11, 12].map((c) => (
            <button key={c} onClick={() => setCls(c)} data-testid={`onboarding-class-${c}`}
              className={`py-3 rounded-xl border font-bold transition-colors ${cls === c ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
              {c}
            </button>
          ))}
        </div>

        {err && <p className="text-red-300 text-xs mb-4" data-testid="onboarding-error">⚠ {err}</p>}

        <button onClick={save} disabled={saving} data-testid="onboarding-submit-btn"
          className="w-full py-3.5 rounded-full font-bold text-black disabled:opacity-60 transition-opacity"
          style={{ background: "linear-gradient(135deg, #00F0FF, #22D3EE)" }}>
          {saving ? <Loader2 size={16} className="animate-spin inline"/> : "Start learning →"}
        </button>
      </div>
    </div>
  );
};
